/**
 * Responsive Component
 *
 * A reusable component for responsive breakpoint selection across control groups.
 * Provides consistent interface for switching between desktop, tablet, and mobile views.
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import './style.scss';

import type { ResponsiveBreakpoint } from '@redfox-thank-you/control-groups/common';
import { Button, Dropdown, MenuGroup, MenuItem } from '@wordpress/components';
import { useDispatch, useSelect } from '@wordpress/data';
import { store as editorStore } from '@wordpress/editor';
import { __ } from '@wordpress/i18n';
import { desktop, mobile, tablet } from '@wordpress/icons';
import classNames from 'classnames';
import React from 'react';

import type { ResponsiveProps } from './types';

/**
 * Responsive dropdown options
 */
const RESPONSIVE_OPTIONS = [
	{
		label: __( 'Desktop', 'redfox-thank-you' ),
		value: 'desktop' as const,
		icon: desktop,
	},
	{
		label: __( 'Tablet', 'redfox-thank-you' ),
		value: 'tablet' as const,
		icon: tablet,
	},
	{
		label: __( 'Mobile', 'redfox-thank-you' ),
		value: 'mobile' as const,
		icon: mobile,
	},
];

export const Responsive: React.FC< ResponsiveProps > = ( {
	className = '',
	showLabels = false,
} ) => {
	// Get WordPress editor device type directly
	const deviceType = useSelect( ( select ) => {
		const editorSelect = select(
			editorStore,
		) as unknown as import('@wordpress/editor').EditorStore;
		return editorSelect?.getDeviceType?.() ?? 'Desktop';
	}, [] );

	const { setDeviceType } = useDispatch( editorStore ) as unknown as {
		setDeviceType: ( type: string ) => void;
	};

	// Map WordPress device types to our breakpoint types
	const deviceTypeToBreakpoint = ( type: string ): ResponsiveBreakpoint => {
		switch ( type ) {
			case 'Tablet':
				return 'tablet';
			case 'Mobile':
				return 'mobile';
			default:
				return 'desktop';
		}
	};

	// Map our breakpoint types to WordPress device types
	const breakpointToDeviceType = (
		breakpoint: ResponsiveBreakpoint,
	): string => {
		switch ( breakpoint ) {
			case 'tablet':
				return 'Tablet';
			case 'mobile':
				return 'Mobile';
			default:
				return 'Desktop';
		}
	};

	const activeBreakpoint = deviceTypeToBreakpoint( deviceType as string );
	const onBreakpointChange = ( breakpoint: ResponsiveBreakpoint ) => {
		setDeviceType( breakpointToDeviceType( breakpoint ) );
	};

	// Get the current active option for display
	const activeOption = RESPONSIVE_OPTIONS.find(
		( option ) => option.value === activeBreakpoint,
	);

	return (
		<div
			className={ classNames(
				'redfox-thank-you-responsive',
				{
					'redfox-thank-you-responsive--with-labels': showLabels,
				},
				className,
			) }
			data-testid="responsive-dropdown"
		>
			<Dropdown
				className="redfox-thank-you-responsive__dropdown"
				contentClassName="redfox-thank-you-responsive__dropdown-content"
				popoverProps={ { placement: 'bottom-start' } }
				renderToggle={ ( { isOpen, onToggle } ) => (
					<Button
						className="redfox-thank-you-responsive__toggle"
						onClick={ onToggle }
						icon={ activeOption?.icon }
						iconSize={ 20 }
						text={ showLabels ? activeOption?.label : undefined }
						aria-expanded={ isOpen }
						aria-label={ __(
							'Select responsive breakpoint',
							'redfox-thank-you',
						) }
					>
						{ ! showLabels && (
							<span className="screen-reader-text">
								{ activeOption?.label }
							</span>
						) }
					</Button>
				) }
				renderContent={ () => (
					<MenuGroup>
						{ RESPONSIVE_OPTIONS.map( ( option ) => (
							<MenuItem
								key={ option.value }
								icon={ option.icon }
								iconPosition="left"
								isSelected={ activeBreakpoint === option.value }
								role="menuitemradio"
								onClick={ () =>
									onBreakpointChange( option.value )
								}
								className={
									activeBreakpoint === option.value
										? 'redfox-thank-you-responsive__menu-item--selected'
										: ''
								}
							>
								{ option.label }
							</MenuItem>
						) ) }
					</MenuGroup>
				) }
			/>
		</div>
	);
};
